import {Select} from 'antd'
import {doc_changeCodiDocumentRowType_Api, docs_deleteCodiDocumentRow_Api,} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik, FormikConsumer} from 'formik'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo} from 'react'
import {HiCode} from 'react-icons/hi'
import {CDActionTypes} from 'views/Document'
import CodiDocumentContext, {
    CodiDocumentContentTypes,
    UseConfirmDeleteAction,
    useRowDetails,
} from 'views/Document/CodiDocumentContext'
import {LoadingPlaceholder} from 'views/Document/functions/renderLoading'
import CodiRowContext from 'views/Document/Row/CodiRowContext'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {AdvancedActionGroups} from 'views/NewTemplate/components/AdvancedActionGroups'
import {DefaultActionButton} from 'views/NewTemplate/components/AdvancedActionGroups/ActionGroup'
import {RequiredSystemAdmin} from 'components/RequiredAdmin'
import {NullAsDefault, renderIf} from 'views/Shared'
import CodiDocumentReportGroup from './CodiDocumentReportGroup'
import CodiDocumentUpdateGroup from './CodiDocumentUpdateGroup'

export const UpdateCodiRowType = ({
  params,
  Wrapper = 'div',
  onCancel = NullAsDefault,
  onSuccess = NullAsDefault,
  codi_document_id,
}) => {
  const t = useTranslate()

  const { id: row_id } = params ?? {}

  const { item, isLoading = false } = useRowDetails(row_id)

  const { handleAsyncAction = NullAsDefault } = useAsyncAction({
    apiInfo: doc_changeCodiDocumentRowType_Api,
    query: bindQueryParams([{ id: codi_document_id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.UPDATE, t)
      onSuccess()
    },
    onError: notifyOnError(t),
  })

  const handleSubmit = async (values, { setSubmitting = NullAsDefault }) => {
    const { index_cd, index_typecd } = values || {}
    setTimeout(() => {
      setSubmitting(true)
      handleAsyncAction({
        index_cd,
        index_typecd,
      })
    })
  }

  const initialValues = useMemo(
    () => ({
      index_typecd: undefined,
      index_cd: item?.index_cd,
      current_index_typecd: item?.index_typecd,
    }),
    [item]
  )

  const options = useMemo(
    () =>
      _.filter(
        [
          CodiDocumentContentTypes.ROOT,
          CodiDocumentContentTypes.CHAPTER,
          CodiDocumentContentTypes.APPENDIX,
          CodiDocumentContentTypes.SECTION,
          CodiDocumentContentTypes.ARTICLE,
          CodiDocumentContentTypes.CLAUSE,
          CodiDocumentContentTypes.POINT,
          CodiDocumentContentTypes.PART,
          CodiDocumentContentTypes.CONTENT,
        ].map((value) => ({ value, label: t(value) })),
        ({ value }) => value !== item?.index_typecd
      ),
    [item.index_typecd]
  )

  return (
    <Formik
      validate={(values) => {
        let errors = {}
        if (!values?.index_typecd) {
          errors.index_typecd = t('required field')
        }
        return errors
      }}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      initialValues={initialValues}>
      <Wrapper className="flex flex-col flex-1">
        <div className="w-full flex flex-col">
          <div className="flex justify-center font-bold text-color-000 text-xl uppercase mb-4">
            {t('change type index typecd')}
          </div>
          <LoadingPlaceholder isLoading={isLoading}>
            <div className="flex items-baseline gap-2 border-b mb-2">
              <span className="capitalize italic text-sm text-color-400">
                {`${t('document')} : `}
              </span>
              <span className="flex-1 font-bold text-doc-lg text-primary">
                {[_.get(item, 'root_cd'), _.get(item, 'root_content')].join(
                  ' '
                )}
              </span>
            </div>
            <div className="text-sm space-x-2">
              <span className="text-color-000 font-bold">
                {`${_.get(item, 'short_index_name')} : `}
              </span>
              {Boolean(_.get(item, 'is_html')) ? (
                <div className="flex flex-col flex-center gap-1 py-3 background-100 rounded-lg mt-2">
                  <HiCode
                    size={30}
                    className="text-color-400"
                  />
                  <span className="text-xs text-color-500 italic">
                    {t('here is the HTML code')}
                  </span>
                </div>
              ) : (
                <span className="text-color-100 font-medium">
                  {_.get(item, 'content')}
                </span>
              )}
            </div>
          </LoadingPlaceholder>
        </div>
        <FormikConsumer>
          {({ values }) => (
            <div className="flex items-center gap-2 text-sm italic mt-1 mb-6">
              <span className="font-light text-color-400">{`${t(
                'current index typecd'
              )} : `}</span>
              <span className="font-bold text-color-000">
                {t(values?.current_index_typecd)}
              </span>
            </div>
          )}
        </FormikConsumer>
        <FieldsFactory
          formSchema={[
            {
              title: 'type of index',
              children: {
                name: 'index_typecd',
                component: ({ name, value, onChange }) => (
                  <Select
                    name={name}
                    value={value}
                    options={options}
                    allowClear={true}
                    className="w-full"
                    placeholder={
                      <span className="text-xs italic text-color-400">
                        {t('ex:type-cd')}
                      </span>
                    }
                    onChange={(value) => {
                      onChange(createValue(name, value))
                    }}
                  />
                ),
              },
            },
          ]}
        />
        <FieldsFactory
          formSchema={[
            {
              title: 'index number',
              children: {
                name: 'index_cd',
                component: createControlledFormField({
                  placeholder: 'index number',
                }),
              },
            },
          ]}
        />
        <FormActionBar />
      </Wrapper>
    </Formik>
  )
}

const CodiDocumentReferenceGroup = ({ title, codi_row, description }) => {
  const { index_typecd } = codi_row ?? {}

  const { handleAction = NullAsDefault } = useContext(CodiDocumentContext)

  const options = [
    {
      title: 'supplement',
      label: 'add',
      value: CDActionTypes.ADD_CONTENT,
      type: 'primary',
      className: 'bg-green-600 hover:bg-green-700 text-white',
      description: 'supplement content description',
    },
    {
      title: 'amendment',
      label: 'amendment',
      value: CDActionTypes.AMEND_CONTENT,
      type: 'primary',
      description: 'amendment content description',
    },
    {
      title: 'annulment',
      label: 'annulment',
      value: CDActionTypes.CANCEL_CONTENT,
      type: 'primary',
      className: 'bg-orange-500 hover:bg-orange-600 text-white',
      description: 'annulment content description',
    },
  ]

  const isDisabled = useCallback(
    (action) => {
      if ([CDActionTypes.ADD_CONTENT].includes(action)) {
        return index_typecd === CodiDocumentContentTypes.CONTENT
      }
      return false
    },
    [index_typecd]
  )

  return renderIf(
    Object.values(CodiDocumentContentTypes)
      .filter((name) => name !== CodiDocumentContentTypes.ROOT)
      .includes(index_typecd),
    <AdvancedActionGroups.ActionGroup
      title={title}
      description={description}>
      {options.map(({ value, title, description, ...rest }, index) => (
        <AdvancedActionGroups.SimpleActionWrapper
          key={index}
          title={title}
          description={description}>
          <DefaultActionButton
            onClick={() => {
              handleAction(value, { selected_item: codi_row })
            }}
            disabled={isDisabled(value)}
            description={description}
            {...rest}
          />
        </AdvancedActionGroups.SimpleActionWrapper>
      ))}
    </AdvancedActionGroups.ActionGroup>
  )
}

const CodiDocumentContentGroup = ({ title, codi_row, description }) => {
  const t = useTranslate()

  const { index_typecd } = codi_row ?? {}

  const { item, handleAction = NullAsDefault } = useContext(CodiDocumentContext)

  const codi_document_id = getId(item)

  const options = {
    insert_content: {
      title: 'insert content',
      label: 'insert',
      value: CDActionTypes.INSERT_CONTENT,
      type: 'primary',
      className: 'bg-green-600 hover:bg-green-700 text-white',
      description: 'insert content description',
    },
    edit_content: {
      title: 'edit content',
      label: 'edit',
      value: CDActionTypes.EDIT_CONTENT,
      type: 'primary',
      description: 'edit contents description',
    },
    arrange_content: {
      title: 'arrange content',
      label: 'order',
      value: CDActionTypes.ARRANGE_CONTENT,
      type: 'primary',
      className: 'bg-indigo-500 hover:bg-indigo-600 text-white',
      description: 'arrange content description',
    },
    delete_content: {
      title: 'delete content',
      label: 'delete',
      value: CDActionTypes.DELETE_CONTENT,
      danger: true,
      type: 'primary',
      description: 'delete content description',
    },
    update_content_type: {
      title: 'change type index typecd',
      label: 'change',
      value: CDActionTypes.UPDATE_CODI_ROW_TYPE,
      type: 'primary',
      description: 'change type index typecd description',
    },
  }

  const isDisabled = useCallback(
    (action) => {
      if (
        [CDActionTypes.INSERT_CONTENT, CDActionTypes.ARRANGE_CONTENT].includes(
          action
        )
      ) {
        return index_typecd === CodiDocumentContentTypes.CONTENT
      }
      return false
    },
    [index_typecd]
  )

  return renderIf(
    Object.values(CodiDocumentContentTypes).includes(index_typecd),
    <AdvancedActionGroups.ActionGroup
      title={title}
      description={description}>
      {[
        options.insert_content,
        options.edit_content,
        options.arrange_content,
        options.update_content_type,
      ].map(({ value, title, description, ...rest }, index) => (
        <AdvancedActionGroups.SimpleActionWrapper
          key={index}
          title={title}
          description={description}>
          <DefaultActionButton
            onClick={() => {
              handleAction(value, { selected_item: codi_row })
            }}
            description={description}
            disabled={isDisabled(value)}
            {...rest}
          />
        </AdvancedActionGroups.SimpleActionWrapper>
      ))}
      <UseConfirmDeleteAction
        onError={notifyOnError(t)}
        title="Delete this content"
        message="You want to delete this content"
        apiInfo={docs_deleteCodiDocumentRow_Api}
        onSuccess={() => {
          successNotify(NotificationActionTypes.DELETE, t)
        }}>
        {({ isLoading = false, confirm = NullAsDefault }) => {
          const { value, title, description, ...rest } = options.delete_content
          return (
            <AdvancedActionGroups.SimpleActionWrapper
              title={title}
              description={description}>
              <DefaultActionButton
                onClick={() => {
                  const row_id = codi_row?.id
                  if (row_id) {
                    confirm({}, { row_id, id: codi_document_id })
                  }
                }}
                isLoading={isLoading}
                description={description}
                disabled={isDisabled(value)}
                {...rest}
              />
            </AdvancedActionGroups.SimpleActionWrapper>
          )
        }}
      </UseConfirmDeleteAction>
    </AdvancedActionGroups.ActionGroup>
  )
}

export const CodiDocumentActions = ({ style, className, header = null }) => (
  <RequiredSystemAdmin type={"codi_document"}>
    <div
      style={style}
      className={classNames('flex flex-col gap-2', className)}>
      {header}
      <div className="flex flex-col gap-6">
        <CodiDocumentReportGroup
          title="error report"
          description="error report description"
        />
        <CodiDocumentUpdateGroup
          title="update info book"
          description="update info description"
        />
        <CodiDocumentContext.Consumer>
          {({ storage }) => {
            const codi_row = storage?.selected_item
            return (
              <React.Fragment>
                <CodiDocumentReferenceGroup
                  codi_row={codi_row}
                  title="amendment, supplement, annulment"
                  description="action according to the codi referenced"
                />
                <CodiDocumentContentGroup
                  codi_row={codi_row}
                  title="edit content"
                  description="edit content description"
                />
              </React.Fragment>
            )
          }}
        </CodiDocumentContext.Consumer>
      </div>
      <div className="h-10" />
    </div>
  </RequiredSystemAdmin>
)

export const CodiDocumentRowActions = ({ style, className, header = null }) => (
  <RequiredSystemAdmin type={"codi_document"}>
    <div
      style={style}
      className={classNames('flex flex-col gap-2', className)}>
      {header}
      <CodiRowContext.Consumer>
        {({ item }) => (
          <div className="flex flex-col gap-6">
            <CodiDocumentReferenceGroup
              codi_row={item}
              title="amendment, supplement, annulment"
              description="action according to the codi referenced"
            />
            <CodiDocumentContentGroup
              codi_row={item}
              title="edit content"
              description="edit content description"
            />
          </div>
        )}
      </CodiRowContext.Consumer>
      <div className="h-10" />
    </div>
  </RequiredSystemAdmin>
)
